import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { 
  Headphones, Mic, BookOpen, PenTool, ChevronLeft, ChevronRight, 
  Save, X, CheckCircle, AlertCircle, Info
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import AudioPlayer from "@/components/AudioPlayer";
import AccommodationsToolbar from "@/components/AccommodationsToolbar";
import AuthContext from "@/contexts/AuthContext";
import ExamIntegrity from "@/components/ExamIntegrity";
import SpeechRecorder from "@/components/SpeechRecorder";
import PreTestInstructions from "@/components/PreTestInstructions";
import WritingInput from "@/components/WritingInput";
import Highlighter from "@/components/Highlighter";
import MoiPresence from "@/components/MoiPresence";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const READING_CAN_DO = {
  1: { level: "Entering", desc: "At Level 1, students can identify familiar words when paired with pictures and match icons or images with word labels." },
  2: { level: "Emerging", desc: "At Level 2, students can identify the main topic of simple, illustrated text and follow written directions with visual support." },
  3: { level: "Developing", desc: "At Level 3, students can identify main ideas and supporting details in grade-level text with some support and use context clues for unfamiliar words." },
  4: { level: "Expanding", desc: "At Level 4, students can read and interpret a variety of genres with moderate support and make inferences from text." },
  5: { level: "Bridging", desc: "At Level 5, students can read and analyze grade-level text with minimal support and evaluate arguments in persuasive texts." },
  6: { level: "Reaching", desc: "At Level 6, students comprehend grade-level text independently across all content areas and analyze complex texts for theme and structure." },
};

const StudentExam = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [session, setSession] = useState(null);
  
  // K-2 friendly mode — larger text, bigger buttons, visual progress dots
  // 3-6 mid mode — medium-large text, standard layout
  // 7-12 academic mode — standard text, denser layout
  // Must be defined after session state
  const isK2 = session && ['K', '1', '2'].includes(session.grade_level);
  const isMid = session && ['3-4', '5-6'].includes(session.grade_level);
  const isUpper = session && ['7-8', '9-12'].includes(session.grade_level);
  const tierClass = isK2 ? 'k2-mode' : isMid ? 'mid-mode' : 'upper-mode';
  const isPreview = session?.is_preview === true;
  // v6.32.114 — WIDA accommodations snapshot from session.
  // v6.34.1  — extended with LP (Large Print) + HC (High Contrast).
  // v6.34.18 — extended with ET / RP / MA / LM (P1 accommodations bundle).
  // Canonical names per services/question_variant_resolver.py ui_flags:
  const accommodations = session?.accommodations || [];
  const hasRA = accommodations.includes("RA"); // Read Aloud
  const hasES = accommodations.includes("ES"); // Extended Time (legacy code; resolver flag is extra_time)
  const hasWD = accommodations.includes("WD"); // Word-to-Word Dictionary
  const hasHL = accommodations.includes("HL"); // Highlighter
  const hasLP = accommodations.includes("LP"); // Large Print          (v6.34.1)
  const hasHC = accommodations.includes("HC"); // High Contrast        (v6.34.1)
  const hasET = accommodations.includes("ET"); // Extended Time (1.5×) (v6.34.18)
  const hasRP = accommodations.includes("RP"); // Repeat Audio          (v6.34.18)
  const hasMA = accommodations.includes("MA"); // Manual Audio Control  (v6.34.18)
  const hasLM = accommodations.includes("LM"); // Line Reader / Masking (v6.34.18)
  // v6.34.11 — Moi-Mode Stage 2 (K-2 mascot accommodation; SILENT-default).
  // session.moi_mode is baked by services/moi_mode_resolver at session create time.
  const moiMode = session?.moi_mode || { enabled: false };
  const moiEnabled = moiMode.enabled === true;
  const moiGradeBand = moiMode.grade_band;
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentModality, setCurrentModality] = useState("listening");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  // v6.34.11 — Moi-Mode transition + completion overlay tracking
  const [prevModality, setPrevModality] = useState(null);
  const [moiTransitionVisible, setMoiTransitionVisible] = useState(false);
  const [moiCompleteOverlayVisible, setMoiCompleteOverlayVisible] = useState(false);
  const [answerImageManifest, setAnswerImageManifest] = useState({});

  // Fetch K-2 answer pictograms
  useEffect(() => {
    if (isK2) {
      axios.get(`${API}/blm/answer-images/manifest`)
        .then(res => setAnswerImageManifest(res.data?.manifest || {}))
        .catch(() => {});
    }
  }, [isK2]);

  const modalities = [
    { key: "listening", label: "Listening", icon: Headphones, color: "bg-purple-100 text-purple-700" },
    { key: "speaking", label: "Speaking", icon: Mic, color: "bg-blue-100 text-blue-700" },
    { key: "reading", label: "Reading", icon: BookOpen, color: "bg-green-100 text-green-700" },
    { key: "writing", label: "Writing", icon: PenTool, color: "bg-orange-100 text-orange-700" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch session
        const sessionRes = await axios.get(`${API}/exam-sessions/${sessionId}`);
        setSession(sessionRes.data);

        // Fetch questions — use adaptive endpoint if session is adaptive
        const formId = sessionRes.data.form_id || "A";
        const sessionData = sessionRes.data;

        let questionsRes;
        if (sessionData.is_adaptive && sessionData.adaptive_profile) {
          const ap = sessionData.adaptive_profile;
          questionsRes = await axios.get(`${API}/adaptive-exam-questions`, {
            params: {
              grade_level: sessionData.grade_level,
              form_id: formId,
              listening_diff: ap.listening || 1,
              speaking_diff: ap.speaking || 1,
              reading_diff: ap.reading || 1,
              writing_diff: ap.writing || 1,
              session_id: sessionId, // v6.32.152 — opt-in variant overlay
              ...(sessionData.eld_standards_filter ? { eld_standards: sessionData.eld_standards_filter } : {}),
              ...(sessionData.klus_filter ? { klus: sessionData.klus_filter } : {}),
            }
          });
        } else {
          const qParams = new URLSearchParams({ grade_level: sessionData.grade_level, form_id: formId });
          qParams.set('session_id', sessionId); // v6.32.152 — opt-in variant overlay
          if (sessionData.eld_standards_filter) qParams.set('eld_standards', sessionData.eld_standards_filter);
          if (sessionData.klus_filter) qParams.set('klus', sessionData.klus_filter);
          // v6.38.20 — Entering-Level tier (Path A). Session already carries
          // `tier`; passing it explicitly keeps teacher preview links accurate.
          if (sessionData.tier && sessionData.tier !== 'standard') qParams.set('tier', sessionData.tier);
          questionsRes = await axios.get(`${API}/exam-questions/${sessionData.grade_level}?${qParams.toString()}`);
        }
        setQuestions(questionsRes.data.questions);

        // Fetch existing responses
        const responsesRes = await axios.get(`${API}/responses/${sessionId}`);
        const responseMap = {};
        responsesRes.data.responses.forEach(r => {
          responseMap[r.question_id] = r.student_answer;
        });
        setResponses(responseMap);

        // Check for saved progress (resume support)
        try {
          const progressRes = await axios.get(`${API}/exam-sessions/${sessionId}/progress`);
          if (progressRes.data.has_progress) {
            setCurrentModality(progressRes.data.current_modality || "listening");
            setCurrentQuestionIndex(progressRes.data.current_question_index || 0);
            setElapsedSeconds(progressRes.data.elapsed_seconds || 0);
            setShowInstructions(false); // Skip instructions when resuming
          }
        } catch { /* no saved progress, start fresh */ }
      } catch (error) {
        console.error("Error fetching exam data:", error);
        toast.error("Failed to load exam. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [sessionId]);

  // v6.42.8 — Moi-Mode transition detection. Was previously a hardcoded
  // 6-second setTimeout, which chopped audio that runs 8-12 seconds.
  // Now the banner stays until MoiPresence signals `onComplete` (audio
  // ended OR safety cap hit), so the child hears every word.
  useEffect(() => {
    if (!moiEnabled) return;
    if (prevModality && prevModality !== currentModality) {
      setMoiTransitionVisible(true);
      // Dismissal is now handled by MoiPresence.onComplete below.
    }
    setPrevModality(currentModality);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModality, moiEnabled]);

  // Elapsed time tracker
  useEffect(() => {
    if (loading || !session) return;
    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [loading, session]);

  // Auto-save progress every 30 seconds (skip for preview)
  useEffect(() => {
    if (loading || !session || isPreview) return;
    const interval = setInterval(() => {
      saveProgress(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [loading, session, currentModality, currentQuestionIndex, elapsedSeconds]);

  const saveProgress = async (isPause = false) => {
    if (isPreview) return; // No saving for preview mode
    try {
      await axios.post(`${API}/exam-sessions/${sessionId}/save-progress`, {
        current_modality: currentModality,
        current_question_index: currentQuestionIndex,
        elapsed_seconds: elapsedSeconds,
        status: isPause ? "paused" : session?.status || "in_progress",
      });
      if (isPause) {
        toast.success("Exam paused. You can resume anytime.");
      }
    } catch (error) {
      console.error("Error saving progress:", error);
    }
  };

  const handlePauseExam = async () => {
    await saveProgress(true);
    navigate(user ? '/teacher' : '/grades');
  };

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 
      ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
      : `${m}:${String(s).padStart(2,'0')}`;
  };

  const modalityQuestions = questions.filter(q => q.modality === currentModality);
  const currentQuestion = modalityQuestions[currentQuestionIndex];

  const totalQuestions = questions.length;
  const answeredQuestions = Object.keys(responses).length;
  const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const saveResponse = useCallback(async (questionId, answer) => {
    if (!answer || answer.trim() === "" || isPreview) return;

    setSaving(true);
    try {
      await axios.post(`${API}/responses`, {
        exam_session_id: sessionId,
        question_id: questionId,
        student_answer: answer
      });
    } catch (error) {
      console.error("Error saving response:", error);
    } finally {
      setSaving(false);
    }
  }, [sessionId, isPreview]);

  const handleAnswerChange = (questionId, answer) => {
    setResponses(prev => ({ ...prev, [questionId]: answer }));
    saveResponse(questionId, answer);
  };

  const handleNext = () => {
    if (currentQuestionIndex < modalityQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Move to next modality
      const currentIndex = modalities.findIndex(m => m.key === currentModality);
      if (currentIndex < modalities.length - 1) {
        setCurrentModality(modalities[currentIndex + 1].key);
        setCurrentQuestionIndex(0);
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      // Move to previous modality
      const currentIndex = modalities.findIndex(m => m.key === currentModality);
      if (currentIndex > 0) {
        const prevModality = modalities[currentIndex - 1].key;
        const prevModalityQuestions = questions.filter(q => q.modality === prevModality);
        setCurrentModality(prevModality);
        setCurrentQuestionIndex(prevModalityQuestions.length - 1);
      }
    }
  };

  const handleSubmitExam = async () => {
    try {
      if (isPreview) {
        // Preview mode — delete the session and redirect to dashboard
        try { await axios.delete(`${API}/exam-sessions/${sessionId}`); } catch {}
        toast.success("Preview complete!");
        navigate('/teacher');
        return;
      }
      // Calculate score
      await axios.post(`${API}/exam-sessions/${sessionId}/calculate-score`);
      
      // Update session status
      await axios.patch(`${API}/exam-sessions/${sessionId}`, {
        status: "completed"
      });

      toast.success("Exam submitted successfully!");
      // v6.34.11 — Moi-Mode complete overlay before redirecting to results.
      // Brief celebration screen for K-2 opt-in students; immediate redirect for others.
      if (moiEnabled) {
        setMoiCompleteOverlayVisible(true);
        // Best-effort telemetry write — non-blocking, fail-silent.
        try {
          await axios.post(`${API}/moi-mode/session-event`, {
            session_id: sessionId,
            event: "exam_complete",
          });
        } catch (_e) { /* telemetry endpoint is optional / lightweight */ }
        // v6.42.8 — navigation is now driven by MoiPresence.onComplete
        // (fires when the audio ends or the 25s safety cap hits) so K-2
        // students hear the full celebration instead of a chopped clip.
        return;
      }
      navigate(`/results/${sessionId}`);
    } catch (error) {
      console.error("Error submitting exam:", error);
      toast.error("Failed to submit exam. Please try again.");
    }
  };

  const isLastQuestion = () => {
    const currentModalityIndex = modalities.findIndex(m => m.key === currentModality);
    return currentModalityIndex === modalities.length - 1 && 
           currentQuestionIndex === modalityQuestions.length - 1;
  };

  const isFirstQuestion = () => {
    const currentModalityIndex = modalities.findIndex(m => m.key === currentModality);
    return currentModalityIndex === 0 && currentQuestionIndex === 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!session || !currentQuestion) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Exam Not Found</h2>
          <p className="text-gray-600">This exam session could not be loaded.</p>
        </div>
      </div>
    );
  }

  // Show pre-test instructions before the exam (skip for previews)
  if (showInstructions && !isPreview) {
    return (
      <div data-testid="moi-mode-welcome-screen">
        {moiEnabled && (
          <div className="bg-gradient-to-b from-indigo-50 to-white border-b border-indigo-100 px-4 py-6">
            <MoiPresence
              enabled={moiEnabled}
              gradeBand={moiGradeBand}
              surface="welcome"
            />
          </div>
        )}
        <PreTestInstructions
          gradeLevel={session.grade_level}
          studentName={session.student_name}
          onBeginExam={() => setShowInstructions(false)}
          isDemo={false}
        />
      </div>
    );
  }

  // v6.34.11 — Moi-Mode completion overlay. Brief 6-second celebration before
  // routing to results so K-2 students get encouragement after a long exam.
  if (moiCompleteOverlayVisible) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-indigo-50 via-white to-white"
        data-testid="moi-mode-complete-overlay"
      >
        <MoiPresence
          enabled={moiEnabled}
          gradeBand={moiGradeBand}
          surface="complete"
          onComplete={() => navigate(`/results/${sessionId}`)}
        />
        <div className="mt-6 text-sm text-slate-500">Loading your results…</div>
      </div>
    );
  }

  const CurrentModalityIcon = modalities.find(m => m.key === currentModality)?.icon || Headphones;
  const currentModalityColor = modalities.find(m => m.key === currentModality)?.color || "bg-gray-100";

  return (
    <ExamIntegrity sessionId={sessionId} data-testid="exam-integrity-wrapper">
    <div
      className={`min-h-screen bg-white ${hasLP ? 'lp-mode' : ''} ${hasHC ? 'hc-mode' : ''}`}
      data-testid="student-exam-page"
      data-lp-active={hasLP ? "true" : "false"}
      data-hc-active={hasHC ? "true" : "false"}
    >
      
      {/* Progress Bar */}
      <div className="exam-progress">
        <div className="exam-progress-bar" style={{ width: `${progressPercentage}%` }}></div>
      </div>

      {/* v6.34.11 — Moi-Mode modality transition banner. Auto-dismisses after 6s. */}
      {moiEnabled && moiTransitionVisible && (
        <div
          className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100 px-4 py-3"
          data-testid="moi-mode-transition-banner"
        >
          <MoiPresence
            enabled={moiEnabled}
            gradeBand={moiGradeBand}
            surface="transition"
            modalityHint={currentModality}
            onComplete={() => setMoiTransitionVisible(false)}
          />
        </div>
      )}

      {/* Header */}
      <header className="sticky top-2 z-40 bg-white border-b-2 border-black">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 ${currentModalityColor} flex items-center justify-center`}>
              <CurrentModalityIcon size={20} />
            </div>
            <div>
              <div className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {session.student_name}
              </div>
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <span>Grade {session.grade_level} • {currentModality.charAt(0).toUpperCase() + currentModality.slice(1)}</span>
                {isPreview && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 text-xs font-semibold rounded-full" data-testid="preview-badge">
                    PREVIEW
                  </span>
                )}
                {session.is_adaptive && session.adaptive_profile && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold rounded-full" data-testid="adaptive-badge">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><polygon points="5,0 10,5 5,10 0,5" /></svg>
                    Level {session.adaptive_profile[currentModality] || '—'}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {accommodations.length > 0 && (
              <div className="inline-flex items-center gap-1" data-testid="accommodations-badges">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Accom:</span>
                {accommodations.map((c) => (
                  <span
                    key={c}
                    className="px-1.5 py-0.5 bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold tracking-wide"
                    data-testid={`accommodation-badge-${c}`}
                    title={
                      c === "RA" ? "Read Aloud — audio player on every question" :
                      c === "ES" ? "Extended Time (legacy)" :
                      c === "WD" ? "Word-to-Word Dictionary" :
                      c === "HL" ? "Highlighter tool enabled" :
                      c === "LP" ? "Large Print — 125% font size applied to this exam (v6.34.1)" :
                      c === "HC" ? "High Contrast — black background + bold white text + yellow focus rings (v6.34.1)" :
                      c === "ET" ? "Extended Time — 1.5× standard duration (v6.34.18)" :
                      c === "RP" ? "Repeat Audio — up to 2 plays per Listening item (v6.34.18)" :
                      c === "MA" ? "Manual Audio Control — student-paced playback (v6.34.18)" :
                      c === "LM" ? "Line Reader / Masking — focus overlay on current question (v6.34.18)" : c
                    }
                  >{c}</span>
                ))}
              </div>
            )}
            <div className="text-sm text-gray-500 font-mono" data-testid="elapsed-time">
              {formatTime(elapsedSeconds)}
            </div>
            <div className="text-sm text-gray-600">
              {answeredQuestions} / {totalQuestions} answered
            </div>
            {saving && !isPreview && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="loading-spinner w-4 h-4"></div>
                Saving...
              </div>
            )}
            {!isPreview && (
              <Button
                variant="outline"
                onClick={handlePauseExam}
                className="btn-sharp h-10 border-amber-400 text-amber-700 hover:bg-amber-50"
                data-testid="pause-exam-btn"
              >
                <Save size={18} className="mr-1" />
                Pause
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowExitDialog(true)}
              className="btn-sharp h-10"
              data-testid="exit-exam-btn"
            >
              <X size={18} className="mr-1" />
              Exit
            </Button>
          </div>
        </div>
      </header>

      {/* Modality Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex overflow-x-auto">
            {modalities.map((modality) => {
              const modalityQs = questions.filter(q => q.modality === modality.key);
              const answeredInModality = modalityQs.filter(q => responses[q.id]).length;
              
              return (
                <button
                  key={modality.key}
                  onClick={() => {
                    setCurrentModality(modality.key);
                    setCurrentQuestionIndex(0);
                  }}
                  className={`modality-tab flex items-center gap-2 ${currentModality === modality.key ? 'active' : ''}`}
                  data-testid={`modality-tab-${modality.key}`}
                >
                  <modality.icon size={18} />
                  <span>{modality.label}</span>
                  {session.is_adaptive && session.adaptive_profile && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7]" data-testid={`adaptive-level-${modality.key}`}>
                      L{session.adaptive_profile[modality.key] || '?'}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    ({answeredInModality}/{modalityQs.length})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className={`exam-container ${tierClass}`}>
        {/* Teacher Reads Aloud Banner */}
        {currentQuestion.teacher_reads && (
          <div className="bg-amber-50 border-2 border-amber-300 p-4 mb-4 flex items-start gap-3" data-testid="teacher-reads-banner">
            <div className="w-8 h-8 bg-amber-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">T</span>
            </div>
            <div>
              <div className="font-bold text-amber-800 text-sm">Teacher: Read Aloud</div>
              <p className="text-xs text-amber-700 mt-0.5">
                This question is designed to be read aloud by the teacher. Please read the passage and question to the student.
              </p>
            </div>
          </div>
        )}
        <div className="question-card animate-fade-in-up" data-testid="question-card">
          {/* v6.32.153 — Accommodation widgets driven by per-question ui_flags
              (RA audio, ES banner, WD glossary). HL is handled below by the
              standalone <Highlighter> wired in v6.32.114. */}
          <AccommodationsToolbar question={currentQuestion} />

          {/* v6.32.114 — WIDA Accommodations: Highlighter (Reading + Writing) */}
          {hasHL && (currentModality === "reading" || currentModality === "writing") && (
            <Highlighter
              clearOnNavigationKey={`${currentModality}-${currentQuestionIndex}`}
              targetSelector='[data-highlightable="true"]'
              enabled={true}
            />
          )}
          {/* v6.32.114 — ES badge: surfaced at the top of every Speaking item so
              the proctoring teacher knows the student gets extended time. */}
          {hasES && currentModality === "speaking" && (
            <div
              className="bg-blue-50 border-2 border-blue-200 px-3 py-2 mb-3 inline-flex items-center gap-2 text-xs font-bold text-blue-700"
              data-testid="es-extended-speaking-badge"
            >
              <Mic size={14} />
              ES — Extended Speaking time approved for this student.
            </div>
          )}
          <div data-highlightable="true">
          {/* Question Header */}
          <div className="flex items-center justify-between mb-6">
            <div className={`question-number ${isK2 ? 'w-14 h-14 text-xl' : isMid ? 'w-12 h-12 text-lg' : ''}`}>
              {currentQuestionIndex + 1}
            </div>
            {isK2 ? (
              /* K-2: Visual dot progress */
              <div className="flex gap-1.5" data-testid="k2-dots">
                {modalityQuestions.map((_, i) => (
                  <div
                    key={i}
                    className={`rounded-full transition-all ${
                      i === currentQuestionIndex ? 'w-4 h-4 ring-2 ring-[#002FA7] ring-offset-1' :
                      responses[modalityQuestions[i]?.id] ? 'w-3 h-3' : 'w-3 h-3'
                    } ${
                      responses[modalityQuestions[i]?.id] ? 'bg-green-400' :
                      i === currentQuestionIndex ? 'bg-[#002FA7]' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                Question {currentQuestionIndex + 1} of {modalityQuestions.length}
              </div>
            )}
          </div>

          {/* ===== LISTENING: Big Picture Format ===== */}
          {currentModality === "listening" && currentQuestion && (
            <>
              {currentQuestion.big_picture_context && currentQuestion.bp_image_url ? (
                <div className={`flex flex-col ${isK2 ? "" : "lg:flex-row"} gap-4 mb-4`} data-testid="big-picture-context">
                  {/* Left: Image + Context */}
                  <div className={`${isK2 ? "w-full" : "lg:w-1/2"} bg-blue-50 border-2 border-blue-200 flex-shrink-0`}>
                    <div className="w-full overflow-hidden border-b-2 border-blue-200 bg-white">
                      <img 
                        src={currentQuestion.bp_image_url} 
                        alt={currentQuestion.big_picture_context}
                        className={`w-full object-contain bg-gray-50 ${isK2 ? "h-64 sm:h-80 lg:h-96" : "h-48 sm:h-64 lg:h-72"}`}
                        data-testid="big-picture-image"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600">
                          BIG IDEA
                        </div>
                        <div className="flex-1 h-px bg-blue-200" />
                      </div>
                      <p className={`text-gray-800 leading-relaxed ${isK2 ? 'text-lg' : 'text-sm'}`}>
                        {currentQuestion.stimulus?.replace(/^\[Big Picture \d+: /, '').replace(/\]$/, '')}
                      </p>
                      <div className="mt-2 px-2 py-1 bg-blue-50 border border-blue-200 inline-block">
                        <span className="text-[10px] font-bold text-blue-700 tracking-wide uppercase">Scene: </span>
                        <span className="text-xs text-blue-800">{currentQuestion.big_picture_context}</span>
                      </div>
                    </div>
                  </div>
                  {/* Right: Audio + Question + Answers */}
                  <div className="lg:w-1/2 flex flex-col">
                    <AudioPlayer
                      questionId={currentQuestion.id}
                      unlimitedPlays={hasRA}
                      onPlayStart={() => console.log('Audio started')}
                      onPlayEnd={() => console.log('Audio ended')}
                    />
                    <h2 className={`font-semibold mt-4 mb-4 ${isK2 ? 'text-3xl' : isMid ? 'text-xl' : 'text-lg'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {currentQuestion.question_text}
                    </h2>
                    {currentQuestion.question_type === "multiple_choice" && (
                      <RadioGroup
                        value={responses[currentQuestion.id] || ""}
                        onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                        className={isK2 ? "grid grid-cols-2 gap-3" : "space-y-3"}
                        data-testid="answer-options"
                      >
                        {currentQuestion.options?.map((option, index) => {
                          const pictogramUrl = isK2 ? answerImageManifest[option.trim()] : null;
                          return (
                          <div
                            key={index}
                            className={`answer-option ${responses[currentQuestion.id] === option ? 'selected' : ''} ${
                              isK2 ? 'flex-col items-center justify-center text-center min-h-[100px] p-3' : ''
                            }`}
                          >
                            <RadioGroupItem
                              value={option}
                              id={`option-${index}`}
                              className={isK2 ? "w-6 h-6 mb-1" : "w-6 h-6"}
                              data-testid={`answer-option-${index}`}
                            />
                            {isK2 && pictogramUrl && (
                              <img src={pictogramUrl} alt={option} className="w-12 h-12 object-contain mb-1" onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            <Label htmlFor={`option-${index}`} className={`cursor-pointer ${isK2 ? 'text-xl font-bold' : 'flex-1 text-base'}`}>
                              {isK2 ? option : (
                                <><span className="font-semibold mr-2">{String.fromCharCode(65 + index)}.</span>{option}</>
                              )}
                            </Label>
                          </div>
                          );
                        })}
                      </RadioGroup>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {currentQuestion.big_picture_context && (
                    <div className="bg-blue-50 border-2 border-blue-200 mb-4">
                      <div className="p-4">
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-2">BIG IDEA</div>
                        <p className="text-gray-800 leading-relaxed text-sm">
                          {currentQuestion.stimulus?.replace(/^\[Big Picture \d+: /, '').replace(/\]$/, '')}
                        </p>
                      </div>
                    </div>
                  )}
                  <AudioPlayer
                    questionId={currentQuestion.id}
                    unlimitedPlays={hasRA}
                    onPlayStart={() => console.log('Audio started')}
                    onPlayEnd={() => console.log('Audio ended')}
                  />
                </>
              )}
            </>
          )}

          {/* ===== SPEAKING: Scaffolded Task Format ===== */}
          {currentModality === "speaking" && currentQuestion && (
            <>
              {currentQuestion.task_name && (
                <div className="bg-sky-50 border-2 border-sky-200 p-5 mb-4" data-testid="speaking-task-header">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-sky-600 mb-1">
                        SPEAKING TASK
                      </div>
                      <div className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {currentQuestion.task_name}
                      </div>
                    </div>
                    {currentQuestion.scaffold_level && (
                      <div className={`px-3 py-1 text-xs font-bold border-2 ${
                        currentQuestion.scaffold_level === 1
                          ? 'bg-sky-100 border-sky-400 text-sky-800'
                          : 'bg-gray-100 border-gray-300 text-gray-600'
                      }`} data-testid="scaffold-level-badge">
                        {currentQuestion.scaffold_level === 1 ? 'Main Question' : `Support Q${currentQuestion.scaffold_level}`}
                      </div>
                    )}
                  </div>
                  {currentQuestion.scaffold_level === 1 && (
                    <p className="text-xs text-sky-600 mt-2">
                      Answer in complete sentences. You will also get simpler follow-up questions.
                    </p>
                  )}
                  {currentQuestion.scaffold_level > 1 && (
                    <p className="text-xs text-gray-500 mt-2">
                      This is a simpler version to help you answer.
                    </p>
                  )}
                </div>
              )}
              {/* Speaking Picture Prompt */}
              {currentQuestion.graphic_support_url && (
                <div className="mb-4 border-2 border-blue-200 overflow-hidden" data-testid="speaking-picture-prompt">
                  <img src={currentQuestion.graphic_support_url} alt="Picture prompt" className={`w-full object-contain bg-gray-50 ${isK2 ? 'h-48 sm:h-64' : 'h-40 sm:h-52'}`} />
                  <div className="bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">Look at the picture above. Use it to help you respond.</div>
                </div>
              )}
            </>
          )}

          {/* ===== READING: Passage-Based Format ===== */}
          {currentModality === "reading" && currentQuestion && (
            <>
              {/* Side-by-side layout for reading with Big Picture image (K-2) */}
              {currentQuestion.bp_image_url ? (
                <div className={`flex flex-col ${isK2 ? "" : "lg:flex-row"} gap-4 mb-4`} data-testid="reading-image-passage">
                  {/* Left: Image */}
                  <div className={`${isK2 ? "w-full" : "lg:w-1/2"} bg-emerald-50 border-2 border-emerald-200 flex-shrink-0`}>
                    <div className="w-full overflow-hidden border-b-2 border-emerald-200 bg-white">
                      <img 
                        src={currentQuestion.bp_image_url} 
                        alt={currentQuestion.big_picture_context || "Scene"}
                        className={`w-full object-contain bg-gray-50 ${isK2 ? "h-64 sm:h-80 lg:h-96" : "h-48 sm:h-64 lg:h-72"}`}
                        data-testid="big-picture-image"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 ${isK2 && currentQuestion.wida_level <= 2 ? 'text-sm' : ''}`}>
                          {currentQuestion.wida_level === 1
                            ? 'READ THE WORD'
                            : currentQuestion.wida_level === 2
                              ? 'READ THE SENTENCE'
                              : 'READ THE PASSAGE'}
                        </div>
                        {currentQuestion.wida_level && READING_CAN_DO[currentQuestion.wida_level] && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 border border-emerald-300 text-emerald-700 text-[10px] font-bold tracking-wide uppercase hover:bg-emerald-200 transition-colors cursor-help" data-testid="reading-can-do-badge">
                                  <Info size={12} />
                                  L{currentQuestion.wida_level} {READING_CAN_DO[currentQuestion.wida_level].level}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs bg-emerald-900 text-white px-4 py-3 text-sm leading-relaxed">
                                <p className="font-bold mb-1">Reading Proficiency — Level {currentQuestion.wida_level}</p>
                                <p>{READING_CAN_DO[currentQuestion.wida_level].desc}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <div className="flex-1 h-px bg-emerald-200" />
                      </div>
                      {currentQuestion.stimulus && currentQuestion.wida_level > 2 && (
                        <p className={`text-gray-800 leading-relaxed ${isK2 ? 'text-lg' : 'text-sm'}`}>
                          {currentQuestion.stimulus
                            .replace(/^\[Passage: [^\]]+\]\s*/, '')
                            .replace(/\[Image:[^\]]+\]/g, '')
                            .replace(/^Look at the picture\.?\s*/i, '')
                            .replace(/^Look at the picture of the parade\.?\s*/i, '')}
                        </p>
                      )}
                      {currentQuestion.stimulus && currentQuestion.wida_level <= 2 && (
                        <p className={`text-gray-500 italic mt-1 ${isK2 ? 'text-base' : 'text-sm'}`}>
                          {currentQuestion.stimulus}
                        </p>
                      )}
                      {currentQuestion.big_picture_context && (
                        <div className="mt-2 px-2 py-1 bg-emerald-50 border border-emerald-200 inline-block">
                          <span className="text-[10px] font-bold text-emerald-700 tracking-wide uppercase">Topic: </span>
                          <span className="text-xs text-emerald-800">{currentQuestion.big_picture_context}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Right: Question + Answers */}
                  <div className="lg:w-1/2 flex flex-col">
                    <h2 className={`font-semibold mb-4 ${isK2 && currentQuestion.wida_level <= 2 ? 'text-4xl' : isK2 ? 'text-3xl' : isMid ? 'text-xl' : 'text-lg'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {currentQuestion.question_text}
                    </h2>
                    {currentQuestion.question_type === "multiple_choice" && (
                      <RadioGroup
                        value={responses[currentQuestion.id] || ""}
                        onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                        className={isK2 && currentQuestion.wida_level <= 2 ? "grid grid-cols-2 gap-3" : "space-y-3"}
                        data-testid="answer-options"
                      >
                        {currentQuestion.options?.map((option, index) => {
                          const pictogramUrl = isK2 ? answerImageManifest[option.trim()] : null;
                          return (
                          <div
                            key={index}
                            className={`answer-option ${responses[currentQuestion.id] === option ? 'selected' : ''} ${
                              isK2 && currentQuestion.wida_level <= 2 ? 'p-5 text-center flex-col items-center justify-center min-h-[100px]' : ''
                            }`}
                          >
                            <RadioGroupItem
                              value={option}
                              id={`reading-opt-${index}`}
                              className={isK2 && currentQuestion.wida_level <= 2 ? "w-6 h-6 mb-1" : "w-6 h-6"}
                              data-testid={`answer-option-${index}`}
                            />
                            {isK2 && pictogramUrl && (
                              <img src={pictogramUrl} alt={option} className="w-12 h-12 object-contain mb-1" onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            <Label htmlFor={`reading-opt-${index}`} className={`cursor-pointer ${
                              isK2 && currentQuestion.wida_level <= 2 ? 'text-2xl font-bold' : 'flex-1 text-base'
                            }`}>
                              {isK2 && currentQuestion.wida_level <= 2 ? option : (
                                <><span className="font-semibold mr-2">{String.fromCharCode(65 + index)}.</span>{option}</>
                              )}
                            </Label>
                          </div>
                          );
                        })}
                      </RadioGroup>
                    )}
                  </div>
                </div>
              ) : currentQuestion.passage_title ? (
                <div className="bg-emerald-50 border-2 border-emerald-200 mb-4" data-testid="reading-passage">
                  {/* Inline Graphic Support Image */}
                  {currentQuestion.graphic_support_url && (
                    <div className="w-full overflow-hidden border-b-2 border-emerald-200">
                      <img 
                        src={currentQuestion.graphic_support_url} 
                        alt={currentQuestion.passage_title}
                        className="w-full h-48 sm:h-64 object-contain bg-gray-50"
                        data-testid="reading-graphic-support-image"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-600 mb-2">
                      READING PASSAGE — {currentQuestion.passage_title}
                    </div>
                    {currentQuestion.stimulus && (
                      <div className="text-gray-800 leading-relaxed whitespace-pre-line">
                        {currentQuestion.stimulus
                          .replace(/^\[Passage: [^\]]+\]\n*/, '')
                          .replace(/\[Image:[^\]]+\]/g, '')}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* ===== WRITING: Task Format with Rubric ===== */}
          {currentModality === "writing" && currentQuestion && (
            <>
              {currentQuestion.task_name && (
                <div className="bg-amber-50 border-2 border-amber-200 p-5 mb-4" data-testid="writing-task-header">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-amber-600 mb-1">
                    WRITING TASK — {currentQuestion.task_name}
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Points possible: {currentQuestion.max_points} • Scored on Discourse + Vocabulary
                  </p>
                </div>
              )}
            </>
          )}

          {/* Generic Stimulus Fallback (for questions without special format fields) */}
          {currentQuestion.stimulus && !currentQuestion.big_picture_context && !currentQuestion.passage_title && !currentQuestion.task_name && currentModality !== "listening" && currentModality !== "reading" && (
            <div className="bg-gray-50 border-2 border-gray-200 p-6 mb-6">
              <div className="text-sm font-semibold text-gray-500 mb-2">STIMULUS</div>
              <p className="text-gray-700">{currentQuestion.stimulus}</p>
            </div>
          )}

          {/* Per-question support image (visual context alongside Big Picture) */}
          {currentQuestion?.graphic_support_url && currentQuestion?.bp_image_url && (currentModality === 'listening' || currentModality === 'reading') && (
            <div className="mb-4 flex items-center gap-3" data-testid="question-support-image">
              <div className={`border-2 border-blue-200 rounded-lg overflow-hidden flex-shrink-0 ${isK2 ? 'w-40 h-40' : 'w-28 h-28'}`}>
                <img src={currentQuestion.graphic_support_url} alt="Question support" className="w-full h-full object-contain bg-gray-50" />
              </div>
              <div className="text-xs text-blue-600 font-medium italic">Look at this picture to help answer the question.</div>
            </div>
          )}

          {/* ═══ WIDA "S" Sample Question Banner ═══ */}
          {currentQuestionIndex === 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-4 flex items-center gap-2" data-testid="student-sample-banner">
              <div className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">S</div>
              <span className={`text-amber-800 ${isK2 ? 'text-lg' : 'text-xs'}`}><strong>Sample Question:</strong> This question is done together with your teacher as guided practice.</span>
            </div>
          )}

          {/* ═══ WIDA Labeled Images for Writing (Picture A, Picture B) ═══ */}
          {currentModality === 'writing' && currentQuestion?.graphic_support_url && !(currentQuestion?.question_type?.startsWith('k_') || ['g1_sentences','g2_short_narrative'].includes(currentQuestion?.question_type)) && (
            <div className="mb-4 border border-gray-200 rounded-lg p-4" data-testid="student-labeled-images">
              <div className="flex gap-4 items-start">
                <div className="text-center">
                  <div className="text-xs font-bold text-gray-600 mb-1">Picture A</div>
                  <div className={`border-2 border-blue-200 rounded-lg overflow-hidden ${isK2 ? 'w-44 h-44' : 'w-36 h-36'}`}>
                    <img src={currentQuestion.graphic_support_url} alt="Picture A" className="w-full h-full object-contain bg-gray-50" />
                  </div>
                </div>
                {currentQuestion.bp_image_url && currentQuestion.bp_image_url !== currentQuestion.graphic_support_url && (
                  <div className="text-center">
                    <div className="text-xs font-bold text-gray-600 mb-1">Picture B</div>
                    <div className={`border-2 border-blue-200 rounded-lg overflow-hidden ${isK2 ? 'w-44 h-44' : 'w-36 h-36'}`}>
                      <img src={currentQuestion.bp_image_url} alt="Picture B" className="w-full h-full object-contain bg-gray-50" />
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-blue-600 mt-2 italic">Look at the picture{currentQuestion.bp_image_url && currentQuestion.bp_image_url !== currentQuestion.graphic_support_url ? 's' : ''} above.</p>
            </div>
          )}

          {/* Question Text — skip for side-by-side layouts (already rendered in right panel) */}
          {!(currentQuestion?.bp_image_url && (currentModality === "listening" || currentModality === "reading")) && (
            <h2 className={`font-semibold mb-8 ${isK2 ? 'text-3xl' : isMid ? 'text-xl' : 'text-lg'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              {currentQuestion.question_text}
            </h2>
          )}

          {/* Answer Input — skip for side-by-side layouts (already rendered in right panel) */}
          {!(currentQuestion?.bp_image_url && (currentModality === "listening" || currentModality === "reading") && currentQuestion?.question_type === "multiple_choice") && (
          <>
          {currentQuestion.question_type === "multiple_choice" ? (
            <RadioGroup
              value={responses[currentQuestion.id] || ""}
              onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
              className="space-y-3"
              data-testid="answer-options"
            >
              {currentQuestion.options?.map((option, index) => (
                <div
                  key={index}
                  className={`answer-option ${responses[currentQuestion.id] === option ? 'selected' : ''}`}
                >
                  <RadioGroupItem
                    value={option}
                    id={`option-${index}`}
                    className="w-6 h-6"
                    data-testid={`answer-option-${index}`}
                  />
                  <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer text-base">
                    <span className="font-semibold mr-2">{String.fromCharCode(65 + index)}.</span>
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          ) : ["k_labeling", "k_sentence_completion", "g1_sentences", "g2_short_narrative"].includes(currentQuestion.question_type) ? (
            <>
            {/* Writing Picture Prompt */}
            {currentQuestion.graphic_support_url && (
              <div className="mb-4 border-2 border-blue-200 overflow-hidden" data-testid="writing-picture-prompt">
                <img src={currentQuestion.graphic_support_url} alt="Writing prompt" className={`w-full object-contain bg-gray-50 ${isK2 ? 'h-48 sm:h-64' : 'h-40 sm:h-52'}`} />
                <div className="bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">Look at the picture above. Use it to help you write.</div>
              </div>
            )}
            <WritingInput
              key={currentQuestion.id}
              question={currentQuestion}
              value={responses[currentQuestion.id] || ""}
              onChange={(val) => handleAnswerChange(currentQuestion.id, val)}
              isK2={isK2}
              isMid={isMid}
              spellCheck={!hasWD}
            />
            </>
          ) : (
            <div>
              {/* Writing Picture Prompt (fallback path) */}
              {currentQuestion.modality === 'writing' && currentQuestion.graphic_support_url && (
                <div className="mb-4 border-2 border-blue-200 overflow-hidden" data-testid="writing-picture-prompt-fallback">
                  <img src={currentQuestion.graphic_support_url} alt="Writing prompt" className="w-full h-40 sm:h-52 object-contain bg-gray-50" />
                  <div className="bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wide">Look at the picture above. Use it to help you write.</div>
                </div>
              )}
              {/* ═══ Plan Your Writing (Gr 3+, not lists) ═══ */}
              {!isK2 && currentQuestion.modality === 'writing' && !currentQuestion.question_text?.toLowerCase().includes('list') && !currentQuestion.question_text?.toLowerCase().includes('name the') && (
                <div className="border border-dashed border-gray-300 rounded-lg p-4 mb-3 bg-gray-50" data-testid="student-plan-writing">
                  <div className="text-xs font-bold text-gray-600 mb-1">Plan your writing</div>
                  <p className="text-[10px] text-gray-400 mb-2">Organize your ideas before writing.</p>
                  <Textarea placeholder="Notes, outline, or organizer..." spellCheck={!hasWD} className="min-h-[60px] text-sm border-gray-200 bg-white rounded-none resize-none" />
                </div>
              )}
              {/* ═══ Sentence Guidance ═══ */}
              {currentQuestion.modality === 'writing' && (
                <div className={`font-bold text-gray-600 mb-2 ${isK2 ? 'text-lg' : 'text-xs'}`} data-testid="student-writing-guidance">
                  {currentQuestion.question_text?.toLowerCase().includes('list') || currentQuestion.question_text?.toLowerCase().includes('name the')
                    ? 'Write your list below:'
                    : currentQuestion.question_text?.toLowerCase().includes('describe')
                    ? 'Write 2 to 4 sentences.'
                    : isK2 ? 'Write 1 to 3 sentences.'
                    : isUpper ? 'Write 5 to 8 sentences.'
                    : 'Write 3 to 5 sentences.'}
                </div>
              )}
              <Textarea
                placeholder={
                  currentQuestion.question_type === "speaking_prompt"
                    ? "Record your response or describe what you would say..."
                    : "Write your response here..."
                }
                value={responses[currentQuestion.id] || ""}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                spellCheck={!hasWD}
                className="min-h-[200px] border-2 border-gray-300 focus:border-[#002FA7] rounded-none text-base p-4"
                data-testid="text-answer-input"
              />
              {/* ═══ Self-Assessment (Gr 3+, not lists) ═══ */}
              {!isK2 && currentQuestion.modality === 'writing' && !currentQuestion.question_text?.toLowerCase().includes('list') && !currentQuestion.question_text?.toLowerCase().includes('name the') && currentQuestion.question_type !== 'speaking_prompt' && (
                <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="student-self-assessment">
                  <div className="text-xs font-bold text-gray-700 mb-2">Now check your writing. Ask yourself:</div>
                  <div className="grid grid-cols-2 gap-1">
                    {['Did I write a beginning?','Did I support my points with details?','Did I write an ending?','Did I choose the best words?','Did I organize my writing?','Did I make my ideas clear?'].map((item,i) => (
                      <label key={i} className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                        <input type="checkbox" className="w-3 h-3 rounded border-gray-400" />
                        {item}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {currentQuestion.question_type === "speaking_prompt" ? (
                <div className="space-y-4">
                  <SpeechRecorder
                    onTranscription={(text) => {
                      if (text) handleAnswerChange(currentQuestion.id, text);
                    }}
                    questionContext="Record your spoken response using the microphone button below. Your speech will be transcribed automatically."
                    disabled={false}
                  />
                  <div className="text-xs text-gray-400">Or type your response below:</div>
                  <Textarea
                    placeholder="Type your spoken response here as a fallback..."
                    value={responses[currentQuestion.id] || ""}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    spellCheck={!hasWD}
                    className="min-h-[100px] border-2 border-gray-300 focus:border-[#002FA7] rounded-none text-base p-4"
                    data-testid="text-answer-input"
                  />
                </div>
              ) : (
                <div className="mt-3">
                  <p className="text-sm text-gray-500">Points possible: {currentQuestion.max_points}</p>
                  {currentQuestion.scaffold_q2 && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 p-3" data-testid="writing-scaffolds">
                      <div className="text-xs font-bold text-amber-700 mb-1">NEED HELP? Try answering these first:</div>
                      <ul className="text-sm text-amber-800 space-y-1">
                        <li>• {currentQuestion.scaffold_q2}</li>
                        {currentQuestion.scaffold_q3 && <li>• {currentQuestion.scaffold_q3}</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </>
          )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={isFirstQuestion()}
            className={`btn-sharp ${isK2 ? 'h-16 px-8 text-lg' : isMid ? 'h-14 px-7 text-base' : 'h-12 px-6'}`}
            data-testid="previous-btn"
          >
            <ChevronLeft size={isK2 ? 24 : 20} className="mr-1" />
            {isK2 ? 'Back' : 'Previous'}
          </Button>

          {!isK2 && (
            <div className={`text-gray-500 ${isMid ? 'text-base' : 'text-sm'}`}>
              {currentModality.charAt(0).toUpperCase() + currentModality.slice(1)} •
              Question {currentQuestionIndex + 1} of {modalityQuestions.length}
            </div>
          )}

          {isLastQuestion() ? (
            <Button
              onClick={() => setShowSubmitDialog(true)}
              className={`btn-sharp-primary ${isK2 ? 'h-16 px-8 text-lg' : isMid ? 'h-14 px-7 text-base' : 'h-12 px-6'}`}
              data-testid="submit-exam-btn"
            >
              <CheckCircle size={isK2 ? 24 : 20} className="mr-1" />
              {isPreview ? 'End Preview' : isK2 ? 'All Done!' : 'Submit Exam'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className={`btn-sharp-primary ${isK2 ? 'h-16 px-8 text-lg' : isMid ? 'h-14 px-7 text-base' : 'h-12 px-6'}`}
              data-testid="next-btn"
            >
              Next
              <ChevronRight size={isK2 ? 24 : 20} className="ml-1" />
            </Button>
          )}
        </div>

        {/* Progress Overview */}
        <div className="mt-12 border-2 border-gray-200 p-6">
          <h3 className="font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Progress Overview</h3>
          <div className="space-y-4">
            {modalities.map((modality) => {
              const modalityQs = questions.filter(q => q.modality === modality.key);
              const answeredInModality = modalityQs.filter(q => responses[q.id]).length;
              const percentage = modalityQs.length > 0 ? (answeredInModality / modalityQs.length) * 100 : 0;
              
              return (
                <div key={modality.key}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="flex items-center gap-2">
                      <modality.icon size={16} />
                      {modality.label}
                    </span>
                    <span className="text-gray-500">{answeredInModality}/{modalityQs.length}</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Exit Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isPreview ? 'End Preview?' : 'Exit Exam?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isPreview
                ? "This is a preview — no data will be saved. You can end it anytime."
                : "Your progress will be saved. You can resume this exam from your dashboard at any time."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isPreview ? 'Continue Preview' : 'Continue Exam'}</AlertDialogCancel>
            {!isPreview && (
              <AlertDialogAction
                onClick={handlePauseExam}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="save-and-exit-btn"
              >
                Save & Exit
              </AlertDialogAction>
            )}
            <AlertDialogAction
              onClick={() => {
                if (isPreview) {
                  axios.delete(`${API}/exam-sessions/${sessionId}`).catch(() => {});
                  navigate('/teacher');
                } else {
                  navigate('/grades');
                }
              }}
              data-testid="exit-without-save-btn"
            >
              {isPreview ? "End Preview" : "Exit Without Saving"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isPreview ? 'End Preview?' : 'Submit Exam?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isPreview ? (
                <>
                  You have viewed {answeredQuestions} of {totalQuestions} questions.
                  <span className="block mt-2 text-gray-500">
                    This is a preview — no data will be saved or scored.
                  </span>
                </>
              ) : (
                <>
                  You have answered {answeredQuestions} of {totalQuestions} questions.
                  {answeredQuestions < totalQuestions && (
                    <span className="block mt-2 text-yellow-600">
                      Warning: {totalQuestions - answeredQuestions} questions are unanswered.
                    </span>
                  )}
                  Once submitted, you cannot make changes. Are you sure?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isPreview ? 'Continue Preview' : 'Continue Exam'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmitExam}>
              {isPreview ? 'End Preview' : 'Submit Exam'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </ExamIntegrity>
  );
};

export default StudentExam;
